import { resolveInferenceEngine } from "../../../../../../src/shared/resolveInferenceEngine";
import { ErrorMessage, Field, useFormikContext, getIn } from "formik";
import { get } from "lodash";
import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import "rc-slider/assets/index.css";
import { HighlightedPromptTextarea } from "../../HighlightedPromptTextarea";
import { availableEngines, DEPRECATED_STORY_BIBLE_KEYS, STORY_BIBLE_KEYS } from "../../../../../../src/shared/schemas/PluginSchema"; // Client version of availableEngines
import IconButton from "../../../IconButton";
import { SudoTooltip } from "../../../../sudokit/components/SudoTooltip";
import { ModelSelectDropdown } from "./ModelSelectDropdown";
import { AdvancedSettingsPanel } from "./AdvancedSettingsPanel";

import { variableDescription, variableLabel } from "../utils";
import { Container } from "./styles";

const formattedEngineOptions = Object.entries(availableEngines)
  .filter(([_key, value]) => !value.hidden)
  .map(([key, value]) => ({
    value: key,
    label: key,
    subLabel: value.description,
  }));
const MAX_NUM_GENERATIONS = 3;
const parseNumberWithFallback = (value, fallback) => {
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return fallback;
  return parsed;
};

const formatSliderValue = (value, decimals = 2) => {
  if (!Number.isFinite(value)) return "0";
  const fixedValue = Number(value).toFixed(decimals);
  if (decimals === 0) {
    return fixedValue;
  }
  const trimmedValue = fixedValue.replace(/\.?0+$/, "");
  return trimmedValue || "0";
};

const sliderVisualProps = {
  dots: false,
  railStyle: { backgroundColor: "var(--slider-rail)", height: 8 },
  trackStyle: { backgroundColor: "var(--slider-track)", height: 8 },
  handleStyle: {
    width: 18,
    height: 18,
    marginTop: -5,
    borderColor: "var(--slider-track)",
    borderWidth: 2,
    boxShadow: "var(--slider-handle-drop-shadow)",
    opacity: 1,
    backgroundColor: "var(--menu-color)",
  },
};
const ADVANCED_SETTING_DEFAULTS = {
  temperature: 0.85,
  frequencyPenalty: 0,
  presencePenalty: 0.25,
  maxTokens: 2000,
};
const DEFAULT_ENGINE_MAX_TOKENS = 4096;

// Function to get all possible available variables for a plugin
const getAllPossibleVariables = (blocks, currentBlockId) => {
  const allVariables = new Set();
  const currentBlock = blocks.find((b) => b.id === currentBlockId);

  blocks.forEach((block) => {
    // Stop when we reach the current block
    if (block.id === currentBlockId) {
      return;
    }

    // Add variables from input document block
    if (block.blockType === "input_document") {
      const possibleDocumentVars = ["preceding_text", "following_text", "highlighted_text", "previous_document_text"];
      possibleDocumentVars.forEach((variable) => allVariables.add(variable));

      // Always show user_text_input in Available Variables; using it still requires enabling the toggle
      allVariables.add("user_text_input");
    }

    // Add variables from input story bible block
    if (block.blockType === "input_story_bible") {
      STORY_BIBLE_KEYS.forEach((variable) => allVariables.add(variable));
    }

    // Add variables from API input block
    if (block.blockType === "api_input") {
      block.options?.variables?.forEach((variable) => {
        allVariables.add(variable.name);
      });
    }

    // Add output from previous inference blocks
    if (block.blockType === "inference") {
      const outputName = block.options?.outputName || "generations";
      allVariables.add(outputName);
      allVariables.add("completionId");
    }
  });

  // Add inputs from the current block (these are variables that have been connected to this block)
  // This is important for multi-stage plugins where the output from one inference block
  // is connected as an input to another inference block with a different name (e.g., prompt_1_result)
  if (currentBlock?.inputs) {
    currentBlock.inputs.forEach((input) => {
      allVariables.add(input.name);
    });
  }

  // Filter out internal variables that shouldn't be user-facing
  const internalVariables = ['generations', 'completionId'];
  return Array.from(allVariables).filter((name) =>
    !DEPRECATED_STORY_BIBLE_KEYS.includes(name) && !internalVariables.includes(name)
  );
};

export const InferenceBlockEditor = ({ block, number, remove }) => {
  const [settingsOpen, setSettingsOpen] = useState(false);

  const { values, errors, setFieldValue } = useFormikContext();
  const blockIndex = values.blocks.findIndex((b) => b.id === block.id);
  const blockDomId = String(block.id).replace(/[^a-zA-Z0-9_-]/g, "-");
  const inferenceBlocks = values.blocks.filter((b) => b.blockType === "inference");

  const optionsPath = `blocks[${blockIndex}].options`;
  const hasOptionsError = !!get(errors, optionsPath);
  const hasPromptError = !!get(errors, `${optionsPath}.prompt`);
  const fieldOptionNameFromBase = (blockPath) => `${optionsPath}.${blockPath}`;

  const currentEngine = resolveInferenceEngine(get(values, fieldOptionNameFromBase("engine")));
  const selectedEngineDefinition = availableEngines[currentEngine] || {};
  const shouldShowTemperature = !selectedEngineDefinition.disallowsTemperature;
  const selectedEngineMaxTokens = selectedEngineDefinition.maxTokens || DEFAULT_ENGINE_MAX_TOKENS;
  const minOutputTokens = Math.min(500, selectedEngineMaxTokens);

  // If current N is set to more than the max the engine can handle, then set to the engine's max
  useEffect(() => {
    const max = availableEngines[currentEngine]?.maxN ? availableEngines[currentEngine].maxN : MAX_NUM_GENERATIONS;

    if (get(values, fieldOptionNameFromBase("numGenerations")) > max) {
      setFieldValue(fieldOptionNameFromBase("numGenerations"), max);
    }
  }, [currentEngine]);

  // Ensure max tokens is not greater than the engine's max tokens.
  // Fires on engine change AND on initial mount (React always fires useEffect after
  // first render), so plugins loaded with a stale value above the now-corrected
  // engine limit are clamped immediately — preventing PluginSchema validation failure
  // when the user tries to save.
  useEffect(() => {
    const max = selectedEngineMaxTokens;

    if (get(values, fieldOptionNameFromBase("maxTokens")) > max) {
      setFieldValue(fieldOptionNameFromBase("maxTokens"), max);
    }
  }, [currentEngine, selectedEngineMaxTokens]);

  useEffect(() => {
    const currentValue = parseNumberWithFallback(
      get(values, fieldOptionNameFromBase("maxTokens")),
      minOutputTokens
    );
    if (currentValue < minOutputTokens) {
      setFieldValue(fieldOptionNameFromBase("maxTokens"), minOutputTokens);
    }
  }, [currentEngine, minOutputTokens]);

  // Generate options for N
  const n = availableEngines[currentEngine]?.maxN ? availableEngines[currentEngine].maxN : MAX_NUM_GENERATIONS;

  const numGenerationOptions = Array.from({ length: n }, (_, i) => ({ label: `${i + 1}`, value: `${i + 1}` }));
  const currentTemperature = parseNumberWithFallback(
    get(values, fieldOptionNameFromBase("temperature")),
    ADVANCED_SETTING_DEFAULTS.temperature
  );
  const currentFrequencyPenalty = parseNumberWithFallback(
    get(values, fieldOptionNameFromBase("frequencyPenalty")),
    ADVANCED_SETTING_DEFAULTS.frequencyPenalty
  );
  const currentPresencePenalty = parseNumberWithFallback(
    get(values, fieldOptionNameFromBase("presencePenalty")),
    ADVANCED_SETTING_DEFAULTS.presencePenalty
  );
  const currentMaxTokens = parseNumberWithFallback(
    get(values, fieldOptionNameFromBase("maxTokens")),
    minOutputTokens
  );
  const defaultMaxTokens = Math.max(
    minOutputTokens,
    Math.min(ADVANCED_SETTING_DEFAULTS.maxTokens, selectedEngineMaxTokens)
  );
  const isTemperatureCustom = Math.abs(currentTemperature - ADVANCED_SETTING_DEFAULTS.temperature) > 0.0001;
  const isFrequencyPenaltyCustom = Math.abs(currentFrequencyPenalty - ADVANCED_SETTING_DEFAULTS.frequencyPenalty) > 0.0001;
  const isPresencePenaltyCustom = Math.abs(currentPresencePenalty - ADVANCED_SETTING_DEFAULTS.presencePenalty) > 0.0001;
  const isMaxTokensCustom = Math.abs(currentMaxTokens - defaultMaxTokens) > 0.5;
  // Compute a slider step that guarantees selectedEngineMaxTokens is exactly on the
  // step grid (i.e., reachable from minOutputTokens in step-sized increments).
  // Without this, RcSlider snaps the max to the nearest grid point, causing values
  // like 32,800 to be stored instead of the true engine max 32,768.
  const maxTokenSliderStep = (() => {
    const range = selectedEngineMaxTokens - minOutputTokens;
    const rawStep = selectedEngineMaxTokens > 10000 ? 100 : selectedEngineMaxTokens > 5000 ? 25 : 1;
    return range % rawStep === 0 ? rawStep : 1;
  })();

  const selectModel = (modelValue) => {
    setFieldValue(fieldOptionNameFromBase("engine"), modelValue);
  };

  // Get all possible user-facing variables (not just current block.inputs)
  const userFacingVariables = getAllPossibleVariables(values.blocks, block.id);

  // Variables the textarea treats as valid.
  // user_text_input is now auto-managed based on prompt usage.
  const validVariables = userFacingVariables;
  // Keep dropdown options aligned with all user-facing variables.
  const availableVariableOptions = userFacingVariables.map((variable) => ({
    name: variable,
    label: variableLabel(variable),
    description: variableDescription(variable),
  }));

  return (
    <Container>
      {(remove || number > 1) && (
        <div className="stageHeader">
          <h3>Step {number}</h3>
          {remove && (
            <IconButton
              onClick={(e) => {
                e.preventDefault();
                remove();
              }}
              src="/assets/delete.svg"
              ariaLabel={`Remove step ${number}`}
            />
          )}
        </div>
      )}

      <div className="instructionRow">
        <h3 className="instructionLabel">Model</h3>
        <div className="instructionField modelField">
          <ModelSelectDropdown
            options={formattedEngineOptions}
            currentValue={currentEngine}
            onSelect={selectModel}
          />
          <IconButton
            onClick={(e) => {
              e.preventDefault();
              setSettingsOpen(!settingsOpen);
            }}
            src="/assets/settings_slider.svg"
            smartFill={false}
            className="advancedSettingsButton"
            ariaLabel="Advanced Settings"
            hoverTipText="Advanced Settings"
            size={32}
            iconSize={18}
          />
        </div>
        <div className="instructionHint">
          <ErrorMessage
            name={fieldOptionNameFromBase("engine")}
            component="p"
            className="error"
          />
        </div>
      </div>

      <AnimatePresence initial={false}>
        {settingsOpen && (
          <motion.div
            className="instructionRow advancedSettingsPlacement advancedSettingsMotion"
            initial={{ opacity: 0, y: -6, height: 0 }}
            animate={{ opacity: 1, y: 0, height: "auto" }}
            exit={{ opacity: 0, y: -6, height: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
          >
            <div className="advancedSettingsSpacer" aria-hidden="true" />
            <div className="instructionField">
              <AdvancedSettingsPanel
                fieldOptionNameFromBase={fieldOptionNameFromBase}
                setFieldValue={setFieldValue}
                currentTemperature={currentTemperature}
                currentFrequencyPenalty={currentFrequencyPenalty}
                currentPresencePenalty={currentPresencePenalty}
                currentMaxTokens={currentMaxTokens}
                showTemperature={shouldShowTemperature}
                showAdaptiveThinking={!!selectedEngineDefinition.supportsBedrockAdaptiveThinkingEffort}
                isTemperatureCustom={isTemperatureCustom}
                isFrequencyPenaltyCustom={isFrequencyPenaltyCustom}
                isPresencePenaltyCustom={isPresencePenaltyCustom}
                isMaxTokensCustom={isMaxTokensCustom}
                selectedEngineMaxTokens={selectedEngineMaxTokens}
                minOutputTokens={minOutputTokens}
                maxTokenSliderStep={maxTokenSliderStep}
                defaultMaxTokens={defaultMaxTokens}
                inferenceBlocksLength={inferenceBlocks.length}
                numGenerationValue={get(values, fieldOptionNameFromBase("numGenerations"))}
                numGenerationOptions={numGenerationOptions}
                formatSliderValue={formatSliderValue}
                sliderVisualProps={sliderVisualProps}
                advancedSettingDefaults={ADVANCED_SETTING_DEFAULTS}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {block.options.prompt.messages.map((message, messageIndex) => {
        const fieldName = `blocks[${blockIndex}].options.prompt.messages[${messageIndex}].template`;
        const fieldError = getIn(errors, fieldName);
        const isSystem = message.role === "system";
        const promptEditorId = `${blockDomId}-${message.role}-${messageIndex}-prompt-editor`;
        const promptLabelId = `${blockDomId}-${message.role}-${messageIndex}-prompt-label`;
        const promptHelpId = `${blockDomId}-${message.role}-${messageIndex}-prompt-help`;
        const caretHintText = isSystem
          ? ""
          : "Type # to pull project context into your instructions.";
        const promptHelpText = isSystem
          ? "Enter the role you want the AI model to play."
          : "Enter your request to the AI model. Type # to open the project context variables menu.";
        const promptPlaceholder = isSystem
          ? "Read the {{highlighted_text}} and then output the kind of reaction that a reader would have when they read this text."
          : "Read the {{highlighted_text}} and then output the kind of reaction that a reader would have when they read this text.\n\nType # to pull project context into your instructions.";
        return (
          <div key={fieldName} className="instructionRow">
            <SudoTooltip
              text={isSystem ? "The role you want the AI model to play." : "Your specific request to the AI model."}
              position="top"
            >
              <h3 id={promptLabelId} className="instructionLabel">{isSystem ? "System" : "User"}</h3>
            </SudoTooltip>
            <div className="instructionField instructionPromptField">
              <p id={promptHelpId} className="sr-only">{promptHelpText}</p>
              <Field name={fieldName}>
                {({ field }) => (
                  <HighlightedPromptTextarea
                    id={promptEditorId}
                    placeholder={promptPlaceholder}
                    caretHintText={caretHintText}
                    className="code"
                    aria-labelledby={promptLabelId}
                    aria-describedby={promptHelpId}
                    validVariables={validVariables}
                    variableOptions={availableVariableOptions}
                    showInsertProjectContextButton={!isSystem}
                    {...field}
                  />
                )}
              </Field>
              {fieldError && <p className="error">{fieldError}</p>}
            </div>
          </div>
        );
      })}
      {!settingsOpen && hasOptionsError && !hasPromptError && (
        <p className="error">
          There are errors with your AI settings. Click the settings icon next to Model to review details.
        </p>
      )}
    </Container>
  );
};
